import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@/components/ui'
import { useAddCategory } from '@/hooks/challenges'
import { findSimilarCategory } from '@/lib/challenges'
import type { ChallengeCategory } from '@/types'

interface AddCategoryFormProps {
  challengeId: string
  categories: ChallengeCategory[]
  mySuggestions: number
  quota: number | null
}

/** Suggest a category; warns when one with a similar title already exists */
export function AddCategoryForm({
  challengeId,
  categories,
  mySuggestions,
  quota,
}: AddCategoryFormProps) {
  const { t } = useTranslation()
  const addCategory = useAddCategory()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const similar = findSimilarCategory(title, categories)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    const position = Math.max(0, ...categories.map((c) => c.position)) + 1
    await addCategory.mutateAsync({ challengeId, title, description, position })
    setTitle('')
    setDescription('')
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-lg border border-dashed border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{t('challenges.categories.suggest')}</p>
        {quota !== null && (
          <span
            className={
              mySuggestions >= quota
                ? 'text-xs font-medium text-success'
                : 'text-xs text-muted-foreground'
            }
          >
            {t('challenges.categories.quota', { done: mySuggestions, quota })}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={title}
          maxLength={120}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('challenges.categories.titlePlaceholder')}
          aria-label={t('challenges.categories.suggest')}
        />
        <Input
          value={description}
          maxLength={500}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('challenges.categories.descriptionPlaceholder')}
          aria-label={t('challenges.categories.descriptionPlaceholder')}
        />
        <Button
          type="submit"
          disabled={!title.trim() || addCategory.isPending}
          className="shrink-0"
        >
          {t('common.add')}
        </Button>
      </div>
      {similar && (
        <p className="text-xs text-warning">
          ⚠️ {t('challenges.categories.similar', { title: similar.title })}
        </p>
      )}
      {addCategory.isError && <p className="text-xs text-error">{t('errors.general')}</p>}
    </form>
  )
}
